import React, { useState } from 'react';
import {
    Box, Typography, Button, TextField, Stack, Pagination, Card, CardContent,
    Grid, Chip, IconButton, InputAdornment, alpha, Dialog, DialogTitle,
    DialogContent, DialogActions
} from '@mui/material';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton as IonicButton, IonIcon } from '@ionic/react';
import { add, search as searchIcon, pricetag, calculator, calendar } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import * as TaxService from '../../../api/tax.service';
import { Tax } from '../../../models/tax';
import { DataTable } from '../../../components/DataTable';
import TaxModal from './TaxModal';
import TaxDetailModal from './TaxDetailModal';

const TaxList: React.FC = () => {
    const history = useHistory();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [taxes, setTaxes] = useState<Tax[]>([]);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({ total: 0, avgRate: 0 });

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedTaxForEdit, setSelectedTaxForEdit] = useState<Tax | null>(null);

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [taxToDelete, setTaxToDelete] = useState<string | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Detail modal state
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedTax, setSelectedTax] = useState<Tax | null>(null);

    // Fetch taxes
    React.useEffect(() => {
        fetchTaxes();
    }, [page, search]);

    const fetchTaxes = async () => {
        setLoading(true);
        setError('');
        try {
            const payload = {
                page,
                limit: 10,
                search: search || ''
            };
            const response = await TaxService.getTaxes(payload);

            if (response && response.data) {
                const taxData = response.data.taxes || response.data || [];
                setTaxes(taxData);
                setPagination({
                    total: response.data.total || taxData.length,
                    totalPages: response.data.pages || 1
                });

                // Calculate stats
                const totalRate = taxData.reduce((sum: number, t: Tax) =>
                    sum + parseFloat(t.rate || '0'), 0);
                setStats({
                    total: taxData.length,
                    avgRate: taxData.length > 0 ? totalRate / taxData.length : 0
                });
            } else {
                setTaxes([]);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch taxes');
            setTaxes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        setTaxToDelete(id);
        setDeleteConfirmText('');
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (taxToDelete && deleteConfirmText.toLowerCase() === 'delete') {
            try {
                await TaxService.deleteTax(taxToDelete);
                setDeleteDialogOpen(false);
                setTaxToDelete(null);
                setDeleteConfirmText('');
                await fetchTaxes();
            } catch (err) {
                console.error('Failed to delete tax:', err);
                alert('Failed to delete tax');
            }
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setTaxToDelete(null);
        setDeleteConfirmText('');
    };

    const handleOpenCreateModal = () => {
        setSelectedTaxForEdit(null);
        setModalOpen(true);
    };

    const handleOpenEditModal = (tax: Tax) => {
        setSelectedTaxForEdit(tax);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedTaxForEdit(null);
    };

    const handleModalSuccess = () => {
        fetchTaxes(); // Refresh the list
    };

    const handleOpenDetailModal = (tax: Tax) => {
        setSelectedTax(tax);
        setDetailModalOpen(true);
    };

    const handleCloseDetailModal = () => {
        setDetailModalOpen(false);
        setSelectedTax(null);
    };

    const handleEditFromDetail = () => {
        if (selectedTax) {
            setDetailModalOpen(false);
            setSelectedTaxForEdit(selectedTax);
            setModalOpen(true);
        }
    };

    const columns = [
        {
            id: 'tax_code',
            label: 'Code',
            minWidth: 120,
            format: (value: string) => (
                <Chip
                    label={value}
                    size="small"
                    sx={{
                        bgcolor: alpha('#8b5cf6', 0.1),
                        color: '#8b5cf6',
                        fontWeight: 600,
                        fontFamily: 'monospace'
                    }}
                />
            )
        },
        {
            id: 'name',
            label: 'Tax Name',
            minWidth: 250,
            format: (value: string) => (
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
                    {value}
                </Typography>
            )
        },
        {
            id: 'rate',
            label: 'Rate',
            minWidth: 120,
            align: 'right' as const,
            format: (value: string) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: '#8b5cf6',
                        fontSize: '0.95rem'
                    }}
                >
                    {Number(value).toFixed(2)}%
                </Typography>
            )
        },
        {
            id: 'tax_type',
            label: 'Type',
            minWidth: 150,
            format: (value: string) => (
                <Chip
                    label={value}
                    size="small"
                    sx={{
                        bgcolor: alpha('#0066cc', 0.1),
                        color: '#0066cc',
                        fontWeight: 500
                    }}
                />
            )
        },
        {
            id: 'created_at',
            label: 'Created',
            minWidth: 120,
            format: (value: string) => (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {new Date(value).toLocaleDateString('en-NG')}
                </Typography>
            )
        },
        {
            id: 'actions',
            label: 'Actions',
            minWidth: 180,
            align: 'center' as const,
            format: (_: any, row: Tax) => (
                <Stack direction="row" spacing={1} justifyContent="center">
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: '#8b5cf6',
                            color: '#8b5cf6',
                            '&:hover': {
                                borderColor: '#7c3aed',
                                bgcolor: alpha('#8b5cf6', 0.05)
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetailModal(row);
                        }}
                    >
                        View
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: '#f59e0b',
                            color: '#f59e0b',
                            '&:hover': {
                                borderColor: '#d97706',
                                bgcolor: alpha('#f59e0b', 0.05)
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(row);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(row.geacloud_taxes_id);
                        }}
                    >
                        Delete
                    </Button>
                </Stack>
            )
        }
    ];

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1);
    };

    return (
        <IonPage className='pageDown'>
            <IonHeader>
                <IonToolbar style={{
                    '--background': 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    '--color': '#ffffff'
                }}>
                    <IonTitle style={{ fontWeight: 600 }}>Tax Management</IonTitle>
                    <IonicButton
                        slot="end"
                        onClick={handleOpenCreateModal}
                        style={{
                            '--background': '#ffffff',
                            '--color': '#8b5cf6',
                            fontWeight: 600
                        }}
                    >
                        <IonIcon icon={add} slot="start" />
                        New Tax
                    </IonicButton>
                </IonToolbar>
            </IonHeader>
            <IonContent style={{ '--background': '#f8fafc' }}>
                <Box sx={{ p: 3 }}>
                    {/* Stats Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)'
                            }}>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <IonIcon icon={pricetag} style={{ fontSize: '2.5rem' }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                {stats.total}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                Total Taxes
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
                            }}>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <IonIcon icon={calculator} style={{ fontSize: '2.5rem' }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                {stats.avgRate.toFixed(2)}%
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                Average Rate
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Search Bar */}
                    <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <CardContent>
                            <TextField
                                fullWidth
                                placeholder="Search taxes by name or code..."
                                variant="outlined"
                                value={search}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IonIcon icon={searchIcon} style={{ fontSize: '1.5rem', color: '#64748b' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: '#f8fafc',
                                        '&:hover': {
                                            bgcolor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </CardContent>
                    </Card>

                    {/* Data Table */}
                    <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                        <CardContent sx={{ p: 0 }}>
                            {loading ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary">Loading taxes...</Typography>
                                </Box>
                            ) : error ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="error">{error}</Typography>
                                </Box>
                            ) : !taxes || taxes.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <IonIcon icon={pricetag} style={{ fontSize: '4rem', color: '#cbd5e1', marginBottom: '1rem' }} />
                                    <Typography color="text.secondary">No taxes found.</Typography>
                                    <Button
                                        variant="contained"
                                        sx={{ mt: 2, bgcolor: '#8b5cf6' }}
                                        onClick={handleOpenCreateModal}
                                    >
                                        Create First Tax
                                    </Button>
                                </Box>
                            ) : (
                                <>
                                    <DataTable
                                        columns={columns}
                                        rows={taxes}
                                        onRowClick={(row) => handleOpenDetailModal(row)}
                                    />

                                    {pagination.totalPages > 1 && (
                                        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', borderTop: '1px solid #e2e8f0' }}>
                                            <Pagination
                                                count={pagination.totalPages}
                                                page={page}
                                                onChange={handlePageChange}
                                                color="primary"
                                                showFirstButton
                                                showLastButton
                                                sx={{
                                                    '& .MuiPaginationItem-root': {
                                                        fontWeight: 500
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ ml: 3, color: 'text.secondary' }}>
                                                Showing {taxes.length} of {pagination.total} taxes
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Tax Modal */}
                <TaxModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    onSuccess={handleModalSuccess}
                    tax={selectedTaxForEdit}
                />

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleCancelDelete}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle sx={{
                        bgcolor: '#dc2626',
                        color: 'white',
                        fontWeight: 700
                    }}>
                        Delete Tax
                    </DialogTitle>
                    <DialogContent sx={{ mt: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Are you sure you want to delete this tax? This action cannot be undone.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            To confirm, please type <strong>delete</strong> below:
                        </Typography>
                        <TextField
                            fullWidth
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="Type 'delete' to confirm"
                            autoFocus
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button
                            onClick={handleCancelDelete}
                            sx={{ borderRadius: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            variant="contained"
                            color="error"
                            disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 600
                            }}
                        >
                            Delete Tax
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Tax Detail Modal */}
                <TaxDetailModal
                    open={detailModalOpen}
                    onClose={handleCloseDetailModal}
                    tax={selectedTax}
                    onEdit={handleEditFromDetail}
                />
            </IonContent>
        </IonPage>
    );
};

export default TaxList;
